/* Типы синаксического дерева */

import { linkChildrenParser } from './linkChildrenParser';

type SyntaxNode = string | SyntaxTagNode;

export type SyntaxTree = SyntaxNode[];

interface SyntaxTagNode {
  tagName?: string;
  attributes?: Record<string, unknown>;
  children?: SyntaxTree;
}

/* Правила преобразования построк в синтаксические ноды */

const singleRules = [
  { pattern: '<br/>', resultTagName: 'br' },
  { pattern: '<br>', resultTagName: 'br' },
  { pattern: '\n', resultTagName: 'br' },
];

export type PairTagChildrenParser = (
  children: SyntaxTree,
  processHref: (href: string) => string,
  additionalLinkParser?: ([url, text]: string[]) => PairTagChildrenParserResult,
) => PairTagChildrenParserResult;

export type PairTagChildrenParserResult =
  | { process: false }
  | {
      process: true;
      children: SyntaxTree;
      attributes?: Record<string, unknown>;
    };

interface PairRule {
  pattern: { start: string; startRegexp?: RegExp; end: string };
  resultTagName?: string;
  parseChildren?: PairTagChildrenParser;
}

const pairRules: PairRule[] = [
  { pattern: { start: '<b>', end: '</b>' }, resultTagName: 'b' },
  { pattern: { start: `'''`, end: `'''` }, resultTagName: 'b' },
  { pattern: { start: '<i>', end: '</i>' }, resultTagName: 'i' },
  { pattern: { start: `''`, end: `''` }, resultTagName: 'i' },
  { pattern: { start: '<s>', end: '</s>' }, resultTagName: 's' },
  { pattern: { start: '<u>', end: '</u>' }, resultTagName: 'u' },
  { pattern: { start: '[', end: ']' }, resultTagName: 'a', parseChildren: linkChildrenParser },
  { pattern: { start: '<span>', end: '</span>' } },
  { pattern: { start: '<ul>', startRegexp: /^<ul.*?>/, end: '</ul>' }, resultTagName: 'ul' },
  { pattern: { start: '<ol>', startRegexp: /^<ol.*?>/, end: '</ol>' }, resultTagName: 'ol' },
  { pattern: { start: '<li>', end: '</li>' }, resultTagName: 'li' },
];

const fakeRule: PairRule = { pattern: { start: '', end: '' }, resultTagName: '' };

const singleRulestFirstChairs = new Set(singleRules.map((item) => item.pattern[0]));
const pairRulesOpenChairs = new Set(pairRules.map((item) => item.pattern.start[0]));

/**
 * Функция, возвращающая синтаксическое дерево из строки. По правилам вики-разметки.
 */
export function getWikiSyntaxTree(
  value: string,
  processHref: (href: string) => string,
  additionalLinkParser?: ([url, text]: string[]) => PairTagChildrenParserResult,
): SyntaxTree {
  function getTagChildrens(parentTagRule: PairRule, fromIndex: number): [SyntaxTree, number | null] {
    const children: SyntaxTree = [];

    let needCommitIndex = fromIndex;
    let cursorIndex = fromIndex;

    cycle: while (cursorIndex < value.length) {
      const char = value[cursorIndex];

      /* Проверка одиночных паттернов */

      if (singleRulestFirstChairs.has(char)) {
        for (const rule of singleRules) {
          if (rule.pattern.length > 0 && rule.pattern === value.substr(cursorIndex, rule.pattern.length)) {
            if (needCommitIndex < cursorIndex) {
              children.push(value.substring(needCommitIndex, cursorIndex));
            }
            children.push({ tagName: rule.resultTagName });
            cursorIndex += rule.pattern.length;
            needCommitIndex = cursorIndex;
            continue cycle;
          }
        }
      }

      /* Проверка на закрывающийся паттерн */

      if (
        parentTagRule.pattern.end.length > 0 &&
        parentTagRule.pattern.end.startsWith(char) &&
        parentTagRule.pattern.end === value.substr(cursorIndex, parentTagRule.pattern.end.length)
      ) {
        if (needCommitIndex < cursorIndex) {
          children.push(value.substring(needCommitIndex, cursorIndex));
        }

        return [children, cursorIndex + parentTagRule.pattern.end.length];
      }

      /* Проверка на открывающийсся паттерн */

      if (pairRulesOpenChairs.has(char)) {
        const checkRule = (
          rule: PairRule,
        ): {
          isFits: boolean;
          startStringLength: number;
        } => {
          if ('startRegexp' in rule.pattern && rule.pattern.startRegexp) {
            const valueFromCurrentToEnd = value.substring(cursorIndex, value.length);
            const ruleMatch = valueFromCurrentToEnd.match(rule.pattern.startRegexp)?.[0];

            return {
              isFits: Boolean(ruleMatch),
              startStringLength: ruleMatch?.length || 0,
            };
          }

          return {
            isFits:
              rule.pattern.start.length > 0 &&
              rule.pattern.start === value.substring(cursorIndex, cursorIndex + rule.pattern.start.length),
            startStringLength: rule.pattern.start.length,
          };
        };

        for (const rule of pairRules) {
          const checkedRule = checkRule(rule);
          if (checkedRule.isFits) {
            if (needCommitIndex < cursorIndex) {
              children.push(value.substring(needCommitIndex, cursorIndex));
            }
            const [tagChildrens, nextCursorIndex] = getTagChildrens(rule, cursorIndex + checkedRule.startStringLength);

            if (nextCursorIndex === null) {
              children.push(rule.pattern.start, ...tagChildrens);

              return [children, null];
            }

            const result = rule.parseChildren?.(tagChildrens, processHref, additionalLinkParser) || {
              process: true,
              children: tagChildrens,
            };

            if (result.process) {
              children.push({
                tagName: rule.resultTagName,
                children: result.children,
                attributes: result.attributes,
              });
            } else {
              children.push(rule.pattern.start, ...tagChildrens, rule.pattern.end);
            }

            cursorIndex = nextCursorIndex;
            needCommitIndex = nextCursorIndex;
            continue cycle;
          }
        }
      }

      cursorIndex += 1;
    }

    if (needCommitIndex < cursorIndex) {
      children.push(value.substring(needCommitIndex, cursorIndex));
    }

    return [children, null];
  }

  return getTagChildrens(fakeRule, fakeRule.pattern.start.length)[0];
}
